import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import Webcam from "react-webcam";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import Starfield from './components/Starfield';
import TestPage from './TestPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate a unique session ID for the current user session
const generateSessionId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Main Home component
const Home = () => {
  // State variables
  const [sessionId, setSessionId] = useState(() => {
    const savedSessionId = localStorage.getItem('galactus_session_id');
    if (savedSessionId) return savedSessionId;
    const newSessionId = generateSessionId();
    localStorage.setItem('galactus_session_id', newSessionId);
    return newSessionId;
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [autoPrompts, setAutoPrompts] = useState([]);
  const [input, setInput] = useState("");
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState([]);
  const [captureInterval, setCaptureInterval] = useState(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentProvider, setCurrentProvider] = useState("openai");
  const [providerOptions, setProviderOptions] = useState({
    openai: ["gpt-4o", "gpt-4o-mini"],
    gemini: ["gemini-2.0-flash", "gemini-1.5-pro"],
    merged: ["openai+gemini"]
  });
  const [selectedModel, setSelectedModel] = useState({
    openai: "gpt-4o",
    gemini: "gemini-2.0-flash",
    merged: "openai+gemini"
  });
  const [lastProviderUsed, setLastProviderUsed] = useState("openai");
  const [isListening, setIsListening] = useState(false);
  const [voiceActivation, setVoiceActivation] = useState(false);
  const [voiceCommandDetected, setVoiceCommandDetected] = useState(false);
  const [savedChats, setSavedChats] = useState(() => {
    const saved = localStorage.getItem('galactus_saved_chats');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSavedChats, setShowSavedChats] = useState(false);
  const [facingMode, setFacingMode] = useState("user"); // "user" for front camera, "environment" for back camera
  const [isListeningToEnvironment, setIsListeningToEnvironment] = useState(false);
  const [environmentAudio, setEnvironmentAudio] = useState([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [liveObservations, setLiveObservations] = useState([]); // For real-time observation feed
  
  // Voice Response States
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("EXAVITQu4vr4xnSDxMaL"); // Default Sarah voice
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioQueue, setAudioQueue] = useState([]);
  
  // Voice Commands States
  const [voiceCommandsEnabled, setVoiceCommandsEnabled] = useState(false);
  const [isListeningForCommand, setIsListeningForCommand] = useState(false);
  const [commandRecognizer, setCommandRecognizer] = useState(null);
  
  // Screen Capture States
  const [isScreenCaptureActive, setIsScreenCaptureActive] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [screenCapture, setScreenCapture] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [inputMessage, setInputMessage] = useState(""); // For mini-chat input in fullscreen mode
  const [isLoading, setIsLoading] = useState(false); // For mini-chat loading state
  
  // Enhanced Screen Capture States
  const [isLiveScreenMonitoring, setIsLiveScreenMonitoring] = useState(false);
  const [screenCaptureInterval, setScreenCaptureInterval] = useState(null);
  const [screenAnalysisMode, setScreenAnalysisMode] = useState("general");
  const [screenObservations, setScreenObservations] = useState([]);
  const [multiModalEnabled, setMultiModalEnabled] = useState(false);
  const [multiModalObservations, setMultiModalObservations] = useState([]);
  
  // Notification state and function  
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      return permission === 'granted';
    }
    return false;
  };

  // Show browser notification
  const showNotification = (title, body, type = 'visual') => {
    if (notificationsEnabled && 'Notification' in window) {
      const icons = {
        'visual': '👁️',
        'screen': '🖥️', 
        'multi-modal': '🌐',
        'audio': '🎵'
      };
      
      new Notification(`${icons[type]} ${title}`, {
        body: body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `galactus-${type}`,
        requireInteraction: false,
        silent: false
      });
    }
  };
  
  const webcamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const miniChatMessagesRef = useRef(null);

  // Toggle environment listening
  const toggleEnvironmentListening = () => {
    if (isListeningToEnvironment) {
      stopEnvironmentListening();
    } else {
      startEnvironmentListening();
    }
  };

  // Start listening to the environment
  const startEnvironmentListening = () => {
    let audioContext;
    let audioAnalyser;
    let audioSource;
    let mediaRecorder;
    let chunks = [];

    // Create audio context
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Get audio stream
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then(stream => {
          console.log("Audio stream acquired successfully");
          setIsListeningToEnvironment(true);
          
          // Connect to analyzer
          audioSource = audioContext.createMediaStreamSource(stream);
          audioAnalyser = audioContext.createAnalyser();
          audioAnalyser.fftSize = 1024;
          audioSource.connect(audioAnalyser);
          
          // Set up recorder
          mediaRecorder = new MediaRecorder(stream);
          console.log("MediaRecorder created, supported:", MediaRecorder.isTypeSupported('audio/webm'));
          
          mediaRecorder.ondataavailable = e => {
            console.log("Audio data available, size:", e.data.size);
            if (e.data.size > 0) {
              chunks.push(e.data);
            }
          };
          
          mediaRecorder.onstop = async () => {
            // Create a blob from the chunks
            const blob = new Blob(chunks, { type: 'audio/webm' });
            chunks = [];
            
            // Convert to base64
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = async () => {
              const base64Audio = reader.result.split(',')[1];
              
              try {
                // Send to backend for analysis
                const formData = new FormData();
                formData.append('audio', base64Audio);
                formData.append('session_id', sessionId);
                
                const response = await axios.post(`${API}/audio/analyze`, formData, {
                  timeout: 30000 // 30 seconds timeout
                });
                
                if (response.data && response.data.observations) {
                  // Add audio observations to the list
                  setEnvironmentAudio(prev => [...response.data.observations, ...prev].slice(0, 10));
                  
                  // Also add to live observations feed for real-time display
                  const newAudioObservations = response.data.observations.map(obs => ({
                    id: Date.now() + Math.random(),
                    content: obs,
                    type: 'audio',
                    timestamp: new Date().toISOString()
                  }));
                  
                  setLiveObservations(prev => [...newAudioObservations, ...prev].slice(0, 5));
                }
              } catch (error) {
                console.error("Error analyzing audio:", error);
              }
            };
          };
          
          // Start recording in 3-second segments
          mediaRecorder.start();
          
          // Store references for cleanup
          window.audioContext = audioContext;
          window.audioSource = audioSource;
          window.audioAnalyser = audioAnalyser;
          window.mediaRecorder = mediaRecorder;
          window.audioStream = stream;
          
          // Set up a loop to stop and restart recording every 5 seconds
          const recordingInterval = setInterval(() => {
            if (window.mediaRecorder && window.mediaRecorder.state === "recording") {
              window.mediaRecorder.stop();
              
              // Wait a bit and start a new recording segment
              setTimeout(() => {
                if (window.mediaRecorder) {
                  chunks = [];
                  window.mediaRecorder.start();
                }
              }, 500);
            }
          }, 5000);
          
          // Store the interval for cleanup
          window.recordingInterval = recordingInterval;
        })
        .catch(err => {
          console.error("Error accessing microphone:", err);
          setIsListeningToEnvironment(false);
          // Show a user-friendly message about microphone permission
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            alert('Microphone access is required for environment audio. Please allow microphone access and try again.');
          } else if (err.name === 'NotFoundError') {
            alert('No microphone found. Please connect a microphone and try again.');
          } else {
            alert('Error accessing microphone: ' + err.message);
          }
        });
    } catch (err) {
      console.error("Error setting up audio context:", err);
      setIsListeningToEnvironment(false);
    }
  };

  // Stop listening to the environment
  const stopEnvironmentListening = () => {
    setIsListeningToEnvironment(false);
    
    // Clean up audio resources
    if (window.recordingInterval) {
      clearInterval(window.recordingInterval);
      window.recordingInterval = null;
    }
    
    if (window.mediaRecorder && window.mediaRecorder.state === "recording") {
      window.mediaRecorder.stop();
    }
    
    if (window.audioSource) {
      window.audioSource.disconnect();
    }
    
    if (window.audioContext) {
      window.audioContext.close().catch(err => console.error("Error closing audio context:", err));
    }
    
    if (window.audioStream) {
      window.audioStream.getTracks().forEach(track => track.stop());
    }
    
    // Clear references
    window.audioContext = null;
    window.audioSource = null;
    window.audioAnalyser = null;
    window.mediaRecorder = null;
    window.audioStream = null;
  };
  
  // Voice recognition setup
  const { transcript, resetTranscript, listening, browserSupportsSpeechRecognition } = useSpeechRecognition({
    commands: [
      {
        command: 'hey galactus',
        callback: () => {
          console.log("Wake word detected: Hey Galactus");
          setVoiceCommandDetected(true);
          // Reset transcript to only capture the command after "Hey Galactus"
          resetTranscript();
        },
        matchInterim: true,
      },
      {
        command: 'stop listening',
        callback: () => {
          stopListening();
        },
      }
    ],
    clearTranscriptOnListen: false,
  });
  
  // When the component mounts, load the conversation history
  useEffect(() => {
    getConversation();
    
    // Check camera permissions immediately
    navigator.mediaDevices.getUserMedia({ video: true })
      .then(() => {
        console.log("Camera permission granted");
      })
      .catch(err => {
        console.error("Camera permission denied", err);
        setAutoPrompts(prev => [...prev, "Please enable camera access to use visual features."]);
      });
    
    // Clean up on component unmount
    return () => {
      if (captureInterval) {
        clearInterval(captureInterval);
      }
      if (isListening) {
        SpeechRecognition.stopListening();
      }
    };
  }, []);
  
  // Handle voice command detection
  useEffect(() => {
    if (voiceCommandDetected && transcript && transcript.trim() !== '') {
      console.log("Processing voice command:", transcript);
      setMessage(transcript);
      setVoiceCommandDetected(false);
      setTimeout(() => {
        sendMessage(transcript);
        resetTranscript();
      }, 1000);
    }
  }, [transcript, voiceCommandDetected]);
  
  // Save chats to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('galactus_saved_chats', JSON.stringify(savedChats));
  }, [savedChats]);
  
  // Start/stop listening
  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };
  
  // Start a new chat
  const startNewChat = () => {
    // Save current chat if it has messages
    if (conversation.length > 0) {
      saveCurrentChat();
    }
    
    // Generate a new session ID
    const newSessionId = generateSessionId();
    localStorage.setItem('galactus_session_id', newSessionId);
    setSessionId(newSessionId);
    
    // Reset state
    setConversation([]);
    setAutoPrompts([]);
    setCurrentImage(null);
    
    // Reset provider to default
    setCurrentProvider("openai");
  };
  
  // Save current chat
  const saveCurrentChat = () => {
    if (conversation.length === 0) return;
    
    const chatTitle = conversation[0]?.content.substring(0, 30) + "..." || `Chat ${savedChats.length + 1}`;
    const chatToSave = {
      id: sessionId,
      title: chatTitle,
      lastMessage: new Date().toISOString(),
      messages: conversation,
      provider: currentProvider
    };
    
    // Check if this chat is already saved
    const existingIndex = savedChats.findIndex(chat => chat.id === sessionId);
    
    if (existingIndex >= 0) {
      // Update existing chat
      const updatedChats = [...savedChats];
      updatedChats[existingIndex] = chatToSave;
      setSavedChats(updatedChats);
    } else {
      // Add new chat
      setSavedChats([...savedChats, chatToSave]);
    }
  };
  
  // Load a saved chat
  const loadChat = (chatId) => {
    if (isProcessing) return;
    
    const chatToLoad = savedChats.find(chat => chat.id === chatId);
    if (!chatToLoad) return;
    
    setSessionId(chatId);
    localStorage.setItem('galactus_session_id', chatId);
    setConversation(chatToLoad.messages);
    setCurrentProvider(chatToLoad.provider || "openai");
    setShowSavedChats(false);
    
    // Reload the latest data from backend to ensure everything is in sync
    getConversation();
  };
  
  // Delete a saved chat
  const deleteChat = (chatId, e) => {
    e.stopPropagation();
    const updatedChats = savedChats.filter(chat => chat.id !== chatId);
    setSavedChats(updatedChats);
    
    // If we're currently viewing the deleted chat, start a new one
    if (sessionId === chatId) {
      startNewChat();
    }
  };
  
  // Start continuous listening
  const startListening = () => {
    SpeechRecognition.startListening({ continuous: true });
    setIsListening(true);
  };
  
  // Stop listening
  const stopListening = () => {
    SpeechRecognition.stopListening();
    setIsListening(false);
    if (transcript) {
      resetTranscript();
    }
  };
  
  // Toggle voice activation mode
  const toggleVoiceActivation = () => {
    if (voiceActivation) {
      setVoiceActivation(false);
      stopListening();
    } else {
      setVoiceActivation(true);
      startListening();
    }
  };
  
  // Auto-scroll to the latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      // Scroll only the conversation container, not the whole page
      const conversationContainer = messagesEndRef.current.parentElement;
      if (conversationContainer) {
        conversationContainer.scrollTop = conversationContainer.scrollHeight;
      }
    }
  }, [conversation]);

  // Fetch conversation history
  const getConversation = async () => {
    try {
      const response = await axios.get(`${API}/conversation/${sessionId}`);
      if (response.data && response.data.messages) {
        setConversation(response.data.messages);
        if (response.data.current_provider) {
          setCurrentProvider(response.data.current_provider);
        }
      }
      
      // Also fetch latest vision data
      const visionResponse = await axios.get(`${API}/vision/latest/${sessionId}`);
      if (visionResponse.data) {
        const newAutoPrompts = visionResponse.data.auto_prompts || [];
        setAutoPrompts(newAutoPrompts);
        setCurrentImage(visionResponse.data.image_data);
        if (visionResponse.data.provider_used) {
          setLastProviderUsed(visionResponse.data.provider_used);
        }
        
        // Update live observations with visual data
        const visualObservations = newAutoPrompts.map(prompt => ({
          id: Date.now() + Math.random(),
          content: prompt,
          type: 'visual',
          timestamp: new Date().toISOString()
        }));
        setLiveObservations(prev => [...visualObservations, ...prev].slice(0, 5));
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
    }
  };

  // Switch between AI providers
  const switchProvider = async (provider) => {
    // Don't switch if it's already the current provider
    if (provider === currentProvider) return;
    
    try {
      // Update the UI immediately for responsiveness
      setCurrentProvider(provider);
      
      // Add system message to conversation to indicate the switch
      setConversation(prev => [...prev, {
        role: "system",
        content: `Switching AI provider to ${provider} (${selectedModel[provider]})...`,
        timestamp: new Date().toISOString()
      }]);
      
      // Send the provider switch request to the backend
      const response = await axios.post(`${API}/provider/switch`, {
        provider: provider,
        model: selectedModel[provider],
        session_id: sessionId
      });
      
      if (response.data && response.data.status === "success") {
        // Update the system message to confirm the switch
        setConversation(prev => {
          const newConversation = [...prev];
          const lastMessage = newConversation[newConversation.length - 1];
          if (lastMessage.role === "system" && lastMessage.content.startsWith("Switching AI provider")) {
            // Update the last message
            newConversation[newConversation.length - 1] = {
              ...lastMessage,
              content: `Switched AI provider to ${provider} (${selectedModel[provider]})`
            };
          }
          return newConversation;
        });
        
        console.log(`Successfully switched to ${provider} provider`);
        
        // Refresh conversation history after switching provider
        await getConversation();
      } else {
        console.error("Provider switch response:", response.data);
      }
    } catch (error) {
      console.error("Error switching provider:", error);
      // Update the system message to indicate an error
      setConversation(prev => {
        const newConversation = [...prev];
        const lastMessage = newConversation[newConversation.length - 1];
        if (lastMessage.role === "system" && lastMessage.content.startsWith("Switching AI provider")) {
          // Update the last message
          newConversation[newConversation.length - 1] = {
            ...lastMessage,
            content: `Failed to switch to ${provider} provider. Using ${currentProvider} instead.`
          };
        }
        return newConversation;
      });
    }
  };

  // Toggle camera activation
  const toggleCamera = () => {
    if (isCameraActive) {
      // Stop the camera
      if (captureInterval) {
        clearInterval(captureInterval);
        setCaptureInterval(null);
      }
      setIsCameraActive(false);
    } else {
      // Start the camera and set up frame capture
      setIsCameraActive(true);
      const interval = setInterval(() => {
        captureFrame();
      }, 8000); // Capture a frame every 8 seconds (much less frequent to avoid rate limits)
      setCaptureInterval(interval);
    }
  };

  // Toggle camera facing mode (front/back)
  const toggleCameraFacing = () => {
    setFacingMode(prev => prev === "user" ? "environment" : "user");
    
    // If the camera is active, we need to restart it with the new facing mode
    if (isCameraActive && captureInterval) {
      // Clear the current interval
      clearInterval(captureInterval);
      
      // Set a new interval after a short delay to allow the camera to switch
      setTimeout(() => {
        const interval = setInterval(() => {
          captureFrame();
        }, 8000); // Maintain the 8 second interval
        setCaptureInterval(interval);
      }, 1000);
    }
  };

  // Capture a frame from the webcam
  const captureFrame = async () => {
    console.log("🎥 captureFrame called - isProcessing:", isProcessing, "webcamRef.current:", !!webcamRef.current);
    
    if (webcamRef.current && !isProcessing) {
      try {
        const imageSrc = webcamRef.current.getScreenshot();
        console.log("📸 Screenshot captured:", imageSrc ? "SUCCESS" : "FAILED", "length:", imageSrc?.length);
        
        if (imageSrc) {
          setCurrentImage(imageSrc);
          console.log("🔄 Processing image...");
          await processImage(imageSrc);
        } else {
          console.error("❌ No image captured from webcam");
          setAutoPrompts(prev => ["Camera capture failed - retrying...", ...prev.filter(p => p !== "Camera capture failed - retrying...").slice(0, 4)]);
        }
      } catch (error) {
        console.error("❌ Error in captureFrame:", error);
        setAutoPrompts(prev => ["Camera error - check permissions", ...prev.filter(p => p !== "Camera error - check permissions").slice(0, 4)]);
      }
    } else {
      console.log("⏸️ Skipping capture - webcamRef:", !!webcamRef.current, "isProcessing:", isProcessing);
    }
  };

  // Process an image with the backend API
  const processImage = async (imageSrc, provider = currentProvider) => {
    console.log("🔍 processImage called - imageSrc length:", imageSrc?.length, "provider:", provider, "isProcessing:", isProcessing);
    
    if (!imageSrc || isProcessing) {
      console.log("⏸️ Skipping processImage - no image or already processing");
      return;
    }
    
    try {
      console.log("📤 Sending image to backend...");
      setIsProcessing(true);
      
      // Remove data:image prefix if present
      const base64Data = imageSrc.includes(',') ? imageSrc.split(',')[1] : imageSrc;
      console.log("📝 Base64 data length:", base64Data.length);
      
      const formData = new FormData();
      formData.append('image', base64Data);
      formData.append('session_id', sessionId);
      formData.append('provider', provider);
      
      console.log("🌐 Making API call to /vision/process...");
      const response = await axios.post(`${API}/vision/process`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000
      });
      
      console.log("✅ API response received:", response.data);
      
      if (response.data && response.data.auto_prompts && response.data.auto_prompts.length > 0) {
        const filteredPrompts = response.data.auto_prompts.filter(p => 
          p && 
          p !== "Processing new image..." && 
          p.trim().length > 0 &&
          !p.startsWith('[SCREEN]')
        );
        
        console.log("📝 Filtered prompts for visual:", filteredPrompts);
        
        if (filteredPrompts.length > 0) {
          setAutoPrompts(prev => {
            const newPrompts = [...filteredPrompts, ...prev.filter(p => 
              p !== "Processing new image..." && 
              p !== "Camera starting - visual analysis will begin shortly..." &&
              p !== "Camera is analyzing..."
            )].slice(0, 10);
            console.log("📋 Updated autoPrompts:", newPrompts);
            return newPrompts;
          });
        }
        
        if (response.data.provider_used) {
          setLastProviderUsed(response.data.provider_used);
        }
      } else {
        console.log("⚠️ No valid prompts in API response");
      }
      
    } catch (error) {
      console.error("❌ Error processing image:", error);
      setAutoPrompts(prev => ["Visual processing error - retrying...", ...prev.slice(0, 4)]);
    } finally {
      setIsProcessing(false);
      console.log("✅ processImage completed");
    }
  };

  // Auto-scroll mini-chat messages to bottom when conversation updates
  useEffect(() => {
    if (miniChatMessagesRef.current && isFullScreen) {
      miniChatMessagesRef.current.scrollTop = miniChatMessagesRef.current.scrollHeight;
    }
  }, [conversation, isFullScreen]);
  
  // Add/remove body class for full-screen mode
  useEffect(() => {
    if (isFullScreen) {
      document.body.classList.add('fullscreen-active');
    } else {
      document.body.classList.remove('fullscreen-active');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('fullscreen-active');
    };
  }, [isFullScreen]);

  // Send a message from mini-chat in full-screen mode
  const sendMiniChatMessage = async () => {
    if (!inputMessage.trim()) return;
    
    // Use the existing sendMessage function with the inputMessage as voiceInput parameter
    await sendMessage(inputMessage);
    
    // Clear the mini-chat input
    setInputMessage("");
  };

  // Auto-fetch observations on component mount and periodically
  useEffect(() => {
    if (sessionId) {
      fetchScreenObservations();
      fetchMultiModalObservations();
      
      // Set up periodic fetching every 10 seconds to get latest observations
      const observationInterval = setInterval(() => {
        if (sessionId) {
          fetchScreenObservations();
          fetchMultiModalObservations();
        }
      }, 10000);
      
      return () => clearInterval(observationInterval);
    }
  }, [sessionId]);

  // More conservative function to extract ONLY truly visual observations from screen data
  const extractVisualFromScreenObservations = () => {
    let extractedVisualObservations = [];
    
    // Check if screen observations contain visual-like content
    screenObservations.forEach(screenObs => {
      if (screenObs.auto_prompts) {
        screenObs.auto_prompts.forEach(prompt => {
          // Very specific visual patterns - only extract if CLEARLY visual/camera content
          const isCameraSpecific = /\b(camera|webcam|video feed|live stream|capture|frame|filming)\b/i.test(prompt);
          const isPersonSpecific = /\b(person|people|individual|human|face|someone sitting|someone standing)\b/i.test(prompt);
          const isEnvironmentSpecific = /\b(room visible|background shows|lighting in|physical environment)\b/i.test(prompt);
          
          // Very specific screen patterns - if ANY of these exist, DON'T extract
          const isDefinitelyScreen = /\b(code|function|variable|class|method|html|css|javascript|react|component|browser|website|application|interface|button|form|div|span|programming|development|syntax)\b/i.test(prompt);
          
          // Only extract if it's clearly visual AND not screen content
          if ((isCameraSpecific || isPersonSpecific || isEnvironmentSpecific) && !isDefinitelyScreen) {
            extractedVisualObservations.push(prompt);
          }
        });
      }
    });
    
    console.log("🔍 Conservative extraction results:", extractedVisualObservations.length, "truly visual observations found");
    return extractedVisualObservations;
  };

  // Function to get pure screen observations (with minimal visual content filtered out)
  const getPureScreenObservations = () => {
    const extractedVisualObs = extractVisualFromScreenObservations();
    
    return screenObservations.map(obs => ({
      ...obs,
      auto_prompts: obs.auto_prompts.filter(prompt => !extractedVisualObs.includes(prompt))
    })).filter(obs => obs.auto_prompts.length > 0);
  };

  // Voice Response Functions
  const fetchAvailableVoices = async () => {
    try {
      const response = await axios.get(`${API}/tts/voices`);
      if (response.data && response.data.voices) {
        setAvailableVoices(response.data.voices);
      }
    } catch (error) {
      console.error("Error fetching voices:", error);
    }
  };

  const speakText = async (text) => {
    if (!voiceEnabled || !text.trim() || isSpeaking) return;
    
    try {
      setIsSpeaking(true);
      
      const response = await axios.post(`${API}/tts/speak`, {
        text: text,
        voice_id: selectedVoice
      }, {
        responseType: 'blob'
      });
      
      // Create audio URL and play
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        console.error("Error playing audio");
      };
      
      await audio.play();
      
    } catch (error) {
      console.error("Error with text-to-speech:", error);
      setIsSpeaking(false);
    }
  };

  // Screen Capture Functions
  const startScreenCapture = async () => {
    try {
      console.log('Starting screen capture...');
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { 
          mediaSource: 'screen',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: true // Include system audio if available
      });
      
      console.log('Screen capture stream acquired');
      setScreenStream(stream);
      setIsScreenCaptureActive(true);
      
      // Handle stream ending (when user stops sharing)
      stream.getVideoTracks()[0].onended = () => {
        console.log('Screen sharing stopped by user');
        stopScreenCapture();
      };
      
      if (voiceEnabled) {
        speakText("Screen capture started. I can now see your screen.");
      }
      
    } catch (error) {
      console.error('Error starting screen capture:', error);
      if (error.name === 'NotAllowedError') {
        alert('Screen sharing permission denied. Please allow screen sharing to use this feature.');
      } else {
        alert('Error starting screen capture: ' + error.message);
      }
    }
  };

  const stopScreenCapture = () => {
    console.log('Stopping screen capture...');
    
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
    }
    
    setIsScreenCaptureActive(false);
    setScreenCapture(null);
    
    if (voiceEnabled) {
      speakText("Screen capture stopped.");
    }
  };

  const captureScreen = async (analysisMode = screenAnalysisMode) => {
    if (!isScreenCaptureActive || !screenStream) {
      console.log('Screen capture not active');
      return;
    }
    
    try {
      console.log(`Capturing screen frame with ${analysisMode} analysis...`);
      
      // Create a video element to capture the screen
      const video = document.createElement('video');
      video.srcObject = screenStream;
      
      // Wait for video to load
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });
      
      // Create canvas and capture frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      // Convert to base64
      const base64Data = canvas.toDataURL('image/jpeg', 0.8);
      
      // Send to backend for enhanced analysis
      setIsProcessing(true);
      
      const response = await axios.post(`${API}/screen-capture`, {
        image_data: base64Data,
        session_id: sessionId,
        analysis_mode: analysisMode,
        provider: currentProvider
      }, {
        timeout: 30000
      });
      
      if (response.data && response.data.status === "processing") {
        console.log(`Screen analysis started with mode: ${analysisMode}`);
        
        // Wait for results and update observations
        setTimeout(() => {
          fetchScreenObservations();
          
          // Trigger multi-modal analysis if enabled
          if (multiModalEnabled && isCameraActive) {
            createMultiModalAnalysis();
          }
        }, 3000);
      }
      
      setIsProcessing(false);
      console.log('Screen capture processed successfully');
      
    } catch (error) {
      console.error('Error capturing screen:', error);
      setIsProcessing(false);
    }
  };

  // Toggle live screen monitoring
  const toggleLiveScreenMonitoring = () => {
    if (isLiveScreenMonitoring) {
      stopLiveScreenMonitoring();
    } else {
      startLiveScreenMonitoring();
    }
  };

  // Start live screen monitoring (automatic capture every 8 seconds)
  const startLiveScreenMonitoring = async () => {
    if (!isScreenCaptureActive) {
      await startScreenCapture();
      // Wait for screen capture to initialize
      setTimeout(() => startLiveScreenMonitoring(), 2000);
      return;
    }

    setIsLiveScreenMonitoring(true);
    
    if (voiceEnabled) {
      speakText("Live screen monitoring started. I'll analyze your screen every 8 seconds.");
    }

    const interval = setInterval(() => {
      if (isScreenCaptureActive && screenStream) {
        captureScreen();
      }
    }, 8000); // Capture every 8 seconds

    setScreenCaptureInterval(interval);
  };

  // Stop live screen monitoring
  const stopLiveScreenMonitoring = () => {
    setIsLiveScreenMonitoring(false);
    
    if (screenCaptureInterval) {
      clearInterval(screenCaptureInterval);
      setScreenCaptureInterval(null);
    }

    if (voiceEnabled) {
      speakText("Live screen monitoring stopped.");
    }
  };

  // Fetch screen observations from backend
  const fetchScreenObservations = async () => {
    try {
      const response = await axios.get(`${API}/screen-observations/${sessionId}?limit=5`);
      if (response.data && response.data.observations) {
        const newObservations = response.data.observations;
        const previousCount = screenObservations.length;
        
        setScreenObservations(newObservations);
        
        // Show notification for new screen observations
        if (newObservations.length > previousCount && notificationsEnabled) {
          const latestObs = newObservations[0];
          if (latestObs && latestObs.auto_prompts && latestObs.auto_prompts.length > 0) {
            showNotification(
              `Screen Analysis (${latestObs.analysis_mode})`, 
              latestObs.auto_prompts[0], 
              'screen'
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching screen observations:", error);
    }
  };

  // Fetch multi-modal observations from backend
  const fetchMultiModalObservations = async () => {
    try {
      const response = await axios.get(`${API}/multi-modal-observations/${sessionId}?limit=3`);
      if (response.data && response.data.observations) {
        const newObservations = response.data.observations;
        const previousCount = multiModalObservations.length;
        
        setMultiModalObservations(newObservations);
        
        // Show notification for new multi-modal observations
        if (newObservations.length > previousCount && notificationsEnabled) {
          const latestObs = newObservations[0];
          if (latestObs && latestObs.combined_analysis && latestObs.combined_analysis.length > 0) {
            showNotification(
              'Multi-Modal Insight', 
              latestObs.combined_analysis[0], 
              'multi-modal'
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching multi-modal observations:", error);
    }
  };

  // Create multi-modal analysis
  const createMultiModalAnalysis = async () => {
    try {
      const response = await axios.post(`${API}/multi-modal-analysis`, {
        session_id: sessionId,
        provider: currentProvider
      });
      
      if (response.data && response.data.status === "processing") {
        console.log("Multi-modal analysis started");
        
        // Wait for results
        setTimeout(() => {
          fetchMultiModalObservations();
        }, 5000);
      }
    } catch (error) {
      console.error("Error creating multi-modal analysis:", error);
    }
  };

  const startScreenRecording = async () => {
    if (!isScreenCaptureActive || !screenStream) {
      await startScreenCapture();
      // Wait a moment for screen capture to initialize
      setTimeout(() => startScreenRecording(), 1000);
      return;
    }
    
    try {
      console.log('Starting screen recording...');
      
      const recorder = new MediaRecorder(screenStream, {
        mimeType: 'video/webm'
      });
      
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        console.log('Recording stopped');
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        
        // Create download link
        const a = document.createElement('a');
        a.href = url;
        a.download = `screen-recording-${Date.now()}.webm`;
        a.click();
        
        URL.revokeObjectURL(url);
        setRecordedChunks([]);
      };
      
      recorder.start();
      setIsRecording(true);
      
      if (voiceEnabled) {
        speakText("Screen recording started.");
      }
      
      // Auto-stop after 30 seconds (can be customized)
      setTimeout(() => {
        if (recorder.state === 'recording') {
          recorder.stop();
          setIsRecording(false);
          if (voiceEnabled) {
            speakText("Screen recording completed and saved.");
          }
        }
      }, 30000);
      
    } catch (error) {
      console.error('Error starting screen recording:', error);
      setIsRecording(false);
    }
  };

  // Voice Commands Functions
  const initializeVoiceCommands = () => {
    console.log('Initializing voice commands...');
    
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.log("Speech recognition not supported");
      alert("Speech recognition not supported in this browser");
      return null;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    
    console.log('Speech recognition configured');
    
    recognition.onstart = () => {
      console.log('Speech recognition started');
      setIsListeningForCommand(true);
    };
    
    recognition.onresult = (event) => {
      const transcript = event.results[event.results.length - 1][0].transcript.toLowerCase().trim();
      console.log('Voice heard:', transcript);
      
      if (transcript.includes('hey galactus')) {
        console.log('Hey Galactus detected!');
        handleVoiceCommand(transcript);
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListeningForCommand(false);
      
      if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please allow microphone access for voice commands.');
      }
    };
    
    recognition.onend = () => {
      console.log('Speech recognition ended');
      setIsListeningForCommand(false);
      
      if (voiceCommandsEnabled) {
        // Restart recognition if commands are enabled
        console.log('Restarting recognition...');
        setTimeout(() => {
          try {
            recognition.start();
          } catch (e) {
            console.log('Recognition restart failed:', e);
          }
        }, 1000);
      }
    };
    
    setCommandRecognizer(recognition);
    console.log('Voice commands initialized');
    
    // Return the recognizer so it can be used immediately
    return recognition;
  };

  const handleVoiceCommand = async (transcript) => {
    console.log('Processing command:', transcript);
    
    // Provide audio feedback
    if (voiceEnabled) {
      speakText("Yes?");
    }
    
    // Camera commands
    if (transcript.includes('switch to back camera') || transcript.includes('back camera')) {
      console.log('Executing: switch to back camera');
      setFacingMode("environment");
      if (voiceEnabled) speakText("Switching to back camera");
    }
    else if (transcript.includes('switch to front camera') || transcript.includes('front camera') || transcript.includes('selfie camera')) {
      console.log('Executing: switch to front camera');
      setFacingMode("user");
      if (voiceEnabled) speakText("Switching to front camera");
    }
    
    // Full-screen commands
    else if (transcript.includes('go full screen') || transcript.includes('full screen mode')) {
      console.log('Executing: go full screen');
      setIsFullScreen(true);
      if (voiceEnabled) speakText("Entering full screen mode");
    }
    else if (transcript.includes('exit full screen') || transcript.includes('leave full screen')) {
      console.log('Executing: exit full screen');
      setIsFullScreen(false);
      if (voiceEnabled) speakText("Exiting full screen mode");
    }
    
    // Environment audio commands
    else if (transcript.includes('start environment audio') || transcript.includes('listen to environment')) {
      console.log('Executing: start environment audio');
      if (!isListeningToEnvironment) {
        toggleEnvironmentListening();
        if (voiceEnabled) speakText("Starting environment audio");
      }
    }
    else if (transcript.includes('stop environment audio') || transcript.includes('stop listening')) {
      console.log('Executing: stop environment audio');
      if (isListeningToEnvironment) {
        toggleEnvironmentListening();
        if (voiceEnabled) speakText("Stopping environment audio");
      }
    }
    
    // Visual analysis commands
    else if (transcript.includes('what do you see') || transcript.includes('describe what you see') || transcript.includes('analyze the scene')) {
      console.log('Executing: visual analysis');
      if (voiceEnabled) speakText("Let me analyze what I can see");
      // Trigger manual capture
      setIsProcessing(true);
      captureFrame(); // Trigger visual analysis
    }
    
    // Screen capture commands
    else if (transcript.includes('capture my screen') || transcript.includes('analyze my screen') || transcript.includes('start screen capture')) {
      console.log('Executing: start screen capture');
      startScreenCapture();
    }
    else if (transcript.includes('start live screen monitoring') || transcript.includes('monitor my screen')) {
      console.log('Executing: start live screen monitoring');
      startLiveScreenMonitoring();
    }
    else if (transcript.includes('stop live screen monitoring') || transcript.includes('stop monitoring')) {
      console.log('Executing: stop live screen monitoring');
      stopLiveScreenMonitoring();
    }
    else if (transcript.includes('analyze screen') || transcript.includes('what\'s on my screen') || transcript.includes('capture screen')) {
      console.log('Executing: analyze screen');
      if (isScreenCaptureActive) {
        captureScreen();
      } else {
        if (voiceEnabled) speakText("Starting screen capture first, then analyzing");
        await startScreenCapture();
        setTimeout(() => captureScreen(), 2000);
      }
    }
    else if (transcript.includes('code review mode') || transcript.includes('analyze this code')) {
      console.log('Executing: code review mode');
      setScreenAnalysisMode('code_review');
      if (voiceEnabled) speakText("Switching to code review mode");
      if (isScreenCaptureActive) {
        captureScreen('code_review');
      }
    }
    else if (transcript.includes('document analysis mode') || transcript.includes('analyze this document')) {
      console.log('Executing: document analysis mode');
      setScreenAnalysisMode('document_analysis');
      if (voiceEnabled) speakText("Switching to document analysis mode");
      if (isScreenCaptureActive) {
        captureScreen('document_analysis');
      }
    }
    else if (transcript.includes('ui analysis mode') || transcript.includes('analyze this interface')) {
      console.log('Executing: UI analysis mode');
      setScreenAnalysisMode('ui_analysis');
      if (voiceEnabled) speakText("Switching to UI analysis mode");
      if (isScreenCaptureActive) {
        captureScreen('ui_analysis');
      }
    }
    else if (transcript.includes('enable multi modal') || transcript.includes('multi modal mode')) {
      console.log('Executing: enable multi-modal');
      setMultiModalEnabled(true);
      if (voiceEnabled) speakText("Multi-modal analysis enabled");
    }
    else if (transcript.includes('disable multi modal') || transcript.includes('stop multi modal')) {
      console.log('Executing: disable multi-modal');
      setMultiModalEnabled(false);
      if (voiceEnabled) speakText("Multi-modal analysis disabled");
    }
    else if (transcript.includes('stop screen capture') || transcript.includes('stop sharing screen')) {
      console.log('Executing: stop screen capture');
      stopScreenCapture();
    }
    else if (transcript.includes('record my screen') || transcript.includes('start recording') || transcript.includes('record this')) {
      console.log('Executing: start screen recording');
      startScreenRecording();
    }
    else if (transcript.includes('explain this code') || transcript.includes('help with this code')) {
      console.log('Executing: code analysis');
      if (voiceEnabled) speakText("Let me analyze the code on your screen");
      setScreenAnalysisMode('code_review');
      if (isScreenCaptureActive) {
        captureScreen('code_review');
      } else {
        await startScreenCapture();
        setTimeout(() => captureScreen('code_review'), 2000);
      }
    }
    
    // Provider switching commands
    else if (transcript.includes('switch to openai') || transcript.includes('use openai')) {
      console.log('Executing: switch to openai');
      setCurrentProvider('openai');
      if (voiceEnabled) speakText("Switching to OpenAI");
    }
    else if (transcript.includes('switch to gemini') || transcript.includes('use gemini')) {
      console.log('Executing: switch to gemini');
      setCurrentProvider('gemini');
      if (voiceEnabled) speakText("Switching to Gemini");
    }
    else if (transcript.includes('use merged mode') || transcript.includes('merged mode')) {
      console.log('Executing: merged mode');
      setCurrentProvider('merged');
      if (voiceEnabled) speakText("Switching to merged mode");
    }
    
    // Conversation commands
    else if (transcript.includes('start new chat') || transcript.includes('new conversation')) {
      console.log('Executing: start new chat');
      startNewChat();
      if (voiceEnabled) speakText("Starting a new conversation");
    }
    
    // Generic query (after "Hey Galactus")
    else {
      const query = transcript.replace(/hey galactus,?\s*/i, '').trim();
      if (query.length > 0) {
        console.log('Executing: send message -', query);
        // Send the query as a message
        await sendMessage(query);
      }
    }
  };

  const toggleVoiceCommands = () => {
    console.log('Toggle voice commands clicked. Current state:', voiceCommandsEnabled);
    
    if (!voiceCommandsEnabled) {
      console.log('Enabling voice commands...');
      
      // Initialize voice commands and get the recognizer directly
      const recognizer = initializeVoiceCommands();
      setVoiceCommandsEnabled(true);
      
      // Start listening after a brief delay using the returned recognizer
      setTimeout(() => {
        if (recognizer) {
          try {
            console.log('Starting speech recognition...');
            recognizer.start();
            setIsListeningForCommand(true);
          } catch (e) {
            console.error('Failed to start voice commands:', e);
            alert('Failed to start voice commands: ' + e.message);
          }
        } else {
          console.error('Command recognizer not available');
        }
      }, 500);
      
      if (voiceEnabled) {
        speakText("Voice commands activated. Say 'Hey Galactus' followed by your command.");
      }
    } else {
      console.log('Disabling voice commands...');
      if (commandRecognizer) {
        try {
          commandRecognizer.stop();
          setIsListeningForCommand(false);
          console.log('Speech recognition stopped');
        } catch (e) {
          console.error('Error stopping speech recognition:', e);
        }
      }
      setVoiceCommandsEnabled(false);
      
      if (voiceEnabled) {
        speakText("Voice commands deactivated");
      }
    }
  };

  // Initialize voice commands on component mount
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (commandRecognizer) {
        commandRecognizer.stop();
      }
    };
  }, [commandRecognizer]);

  // Auto-start camera on component mount
  useEffect(() => {
    // Clear any existing fake/placeholder observations
    setAutoPrompts([]);
    
    // Auto-enable camera after component mounts
    const autoStartCamera = () => {
      if (!isCameraActive) {
        setIsCameraActive(true);
        const interval = setInterval(() => {
          captureFrame();
        }, 8000); // Capture a frame every 8 seconds
        setCaptureInterval(interval);
        
        // Clear fake observations and set a proper empty state
        setAutoPrompts(["Camera starting - visual analysis will begin shortly..."]);
      }
    };
    
    // Start camera after a brief delay to ensure webcam is ready
    const timer = setTimeout(autoStartCamera, 2000);
    
    return () => {
      clearTimeout(timer);
      if (captureInterval) {
        clearInterval(captureInterval);
      }
    };
  }, []); // Empty dependency array = run once on mount

  // Fetch voices on component mount
  useEffect(() => {
    fetchAvailableVoices();
  }, []);

  // Send a message to the AI
  // Enhanced send message function with observation context
  const sendMessage = async (voiceInput = null) => {
    const messageToSend = voiceInput || input;
    
    if (!messageToSend || !messageToSend.trim()) return;
    
    // Create context from all observation types
    let contextInfo = [];
    
    // Add visual observations context
    const visualObs = autoPrompts.filter(prompt => !prompt.startsWith('[SCREEN]') && prompt !== "Processing new image...");
    if (visualObs.length > 0) {
      contextInfo.push(`Visual observations: ${visualObs.slice(0, 3).join(', ')}`);
    }
    
    // Add screen observations context
    if (screenObservations.length > 0) {
      const latestScreen = screenObservations[0];
      if (latestScreen && latestScreen.auto_prompts) {
        contextInfo.push(`Screen analysis (${latestScreen.analysis_mode}): ${latestScreen.auto_prompts.slice(0, 2).join(', ')}`);
      }
    }
    
    // Add multi-modal observations context
    if (multiModalObservations.length > 0) {
      const latestMultiModal = multiModalObservations[0];
      if (latestMultiModal && latestMultiModal.combined_analysis) {
        contextInfo.push(`Multi-modal insights: ${latestMultiModal.combined_analysis.slice(0, 1).join(', ')}`);
      }
    }
    
    // Add audio context if available
    if (environmentAudio.length > 0) {
      contextInfo.push(`Audio context: ${environmentAudio.slice(0, 1).join(', ')}`);
    }
    
    // Create enhanced message with context
    const enhancedMessage = contextInfo.length > 0 
      ? `${messageToSend}\n\nCurrent context: ${contextInfo.join(' | ')}`
      : messageToSend;
    
    // Add the user message to the conversation immediately for UI responsiveness
    setConversation(prev => [...prev, {
      role: "user",
      content: messageToSend,
      timestamp: new Date().toISOString()
    }]);
    
    if (!voiceInput) setInput(""); // Clear the input field only if it's not a voice input
    
    try {
      // Add a temporary "thinking" message
      setConversation(prev => [...prev, {
        role: "assistant",
        content: "Thinking...",
        timestamp: new Date().toISOString(),
        isThinking: true // Flag to identify this is a temporary message
      }]);
      
      // Send the message with a timeout to prevent hanging
      const response = await axios.post(`${API}/conversation/message`, {
        content: enhancedMessage,
        session_id: sessionId,
        provider: currentProvider
      }, {
        timeout: 60000 // 1 minute timeout
      });
      
      // Remove the thinking message and add the real response
      setConversation(prev => {
        // Filter out the temporary thinking message
        const filteredConv = prev.filter(msg => !msg.isThinking);
        
        // Add the AI response
        return [...filteredConv, {
          role: "assistant",
          content: response.data.response,
          timestamp: new Date().toISOString(),
          provider: response.data.provider_used
        }];
      });
      
      // Speak the response if voice is enabled
      if (voiceEnabled && response.data.response) {
        speakText(response.data.response);
      }
      
      // Update auto-prompts if new ones were generated
      if (response.data.auto_prompts && response.data.auto_prompts.length > 0) {
        setAutoPrompts(response.data.auto_prompts);
      }
      
      // Update the last provider used
      if (response.data.provider_used) {
        setLastProviderUsed(response.data.provider_used);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Remove the thinking message and add an error message
      setConversation(prev => {
        // Filter out the temporary thinking message
        const filteredConv = prev.filter(msg => !msg.isThinking);
        
        // Add error message
        return [...filteredConv, {
          role: "assistant",
          content: "I'm sorry, I encountered an error while processing your request. " + 
                   "Please try again or try switching to a different AI provider.",
          timestamp: new Date().toISOString(),
          isError: true
        }];
      });
    }
  };

  // Handle Enter key press in the message input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle model change
  const handleModelChange = (provider, model) => {
    setSelectedModel(prev => ({
      ...prev,
      [provider]: model
    }));
  };

  // Trigger an image capture with a specific provider
  const captureWithProvider = (provider) => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCurrentImage(imageSrc);
        processImage(imageSrc, provider);
      }
    }
  };

  return (
    <div className="galactus-container">
      {/* Header */}
      <header className="galactus-header">
        <div className="header-top">
          <h1>Galactus 3</h1>
          <div className="chat-controls">
            <button 
              className="new-chat-button"
              onClick={startNewChat}
              title="Start a new chat"
            >
              <span className="icon">+</span> New Chat
            </button>
            <button 
              className={`saved-chats-button ${showSavedChats ? 'active' : ''}`}
              onClick={() => setShowSavedChats(!showSavedChats)}
              title="View saved chats"
            >
              <span className="icon">📚</span> 
              {showSavedChats ? 'Hide Chats' : 'Saved Chats'}
            </button>
            <button 
              className="save-current-button"
              onClick={saveCurrentChat}
              disabled={conversation.length === 0}
              title="Save current chat"
            >
              <span className="icon">💾</span> Save Chat
            </button>
          </div>
        </div>
        
        <p className="subtitle">Advanced Cognitive AI System</p>
        
        {/* AI Provider Selector */}
        <div className="provider-selector">
          <div className="provider-buttons">
            <button 
              className={`provider-button ${currentProvider === 'openai' ? 'active' : ''}`}
              onClick={() => switchProvider('openai')}
            >
              <span className="provider-icon">🔵</span>
              <span className="provider-name">OpenAI</span>
            </button>
            
            <button 
              className={`provider-button ${currentProvider === 'gemini' ? 'active' : ''}`}
              onClick={() => switchProvider('gemini')}
            >
              <span className="provider-icon">🟣</span>
              <span className="provider-name">Gemini</span>
            </button>
            
            <button 
              className={`provider-button merged ${currentProvider === 'merged' ? 'active' : ''}`}
              onClick={() => switchProvider('merged')}
            >
              <span className="provider-icon">⚡</span>
              <span className="provider-name">Merged</span>
            </button>
          </div>
          
          <div className="model-selector">
            <select 
              value={selectedModel[currentProvider]} 
              onChange={(e) => handleModelChange(currentProvider, e.target.value)}
              disabled={isProcessing}
            >
              {providerOptions[currentProvider].map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <div className="main-content">
        {/* Saved Chats Panel - conditionally displayed */}
        {showSavedChats && (
          <div className="saved-chats-panel">
            <h3>Saved Conversations</h3>
            
            {savedChats.length > 0 ? (
              <div className="saved-chats-list">
                {savedChats.map(chat => (
                  <div 
                    key={chat.id} 
                    className={`saved-chat-item ${sessionId === chat.id ? 'active' : ''}`}
                    onClick={() => loadChat(chat.id)}
                  >
                    <div className="chat-info">
                      <div className="chat-title">{chat.title}</div>
                      <div className="chat-date">
                        {new Date(chat.lastMessage).toLocaleDateString()}
                      </div>
                    </div>
                    <button 
                      className="delete-chat" 
                      onClick={(e) => deleteChat(chat.id, e)}
                      title="Delete this chat"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-chats">
                No saved conversations yet.
              </div>
            )}
          </div>
        )}
        
        {/* Left panel - Camera feed */}
        <div className="camera-panel">
          <div className="camera-container">
            {isCameraActive ? (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="webcam"
                mirrored={facingMode === "user"}
                videoConstraints={{
                  facingMode: facingMode
                }}
              />
            ) : currentImage ? (
              <div className="static-image-container">
                <img src={currentImage} alt="Last captured frame" className="static-image" />
              </div>
            ) : (
              <div className="camera-placeholder">
                <div className="camera-icon">📷</div>
                <p>Camera inactive</p>
              </div>
            )}
            
            {isProcessing && (
              <div className="processing-indicator">
                <div className="processing-spinner"></div>
                <span>Processing...</span>
              </div>
            )}
          </div>
          
          <div className="camera-controls">
            <button 
              className={`camera-toggle ${isCameraActive ? 'active' : ''}`}
              onClick={toggleCamera}
            >
              {isCameraActive ? 'Disable Camera' : 'Enable Camera'}
            </button>
            
            {isCameraActive && (
              <>
                <button
                  className={`camera-toggle camera-flip ${facingMode === "environment" ? 'back-active' : ''}`}
                  onClick={toggleCameraFacing}
                  title={facingMode === "user" ? "Switch to back camera" : "Switch to front camera"}
                >
                  <span className="camera-flip-icon">🔄</span>
                  {facingMode === "user" ? "Switch to Back Camera" : "Switch to Front Camera"}
                </button>
                
                <button
                  className="camera-toggle fullscreen-toggle"
                  onClick={() => setIsFullScreen(true)}
                  title="Enter full-screen camera mode"
                >
                  <span className="fullscreen-icon">⛶</span>
                  Full Screen
                </button>
                
                <div className="provider-capture-buttons">
                  <button 
                    className="capture-with-provider openai"
                    onClick={() => captureWithProvider('openai')}
                    disabled={isProcessing}
                  >
                    Capture with OpenAI
                  </button>
                  <button 
                    className="capture-with-provider gemini"
                    onClick={() => captureWithProvider('gemini')}
                    disabled={isProcessing}
                  >
                    Capture with Gemini
                  </button>
                </div>
              </>
            )}
          </div>
          
          {/* Split Layout: Left for Visual, Right for Chat + Screen */}
          <div className="split-layout">
            {/* Left Column - Visual Observations Only */}
            <div className="left-column">
              <div className="visual-observations-panel">
                <h3>
                  📷 Visual (Camera)
                  {isCameraActive && <span className="live-indicator">📡 LIVE</span>}
                  {/* Notification Toggle */}
                  <button 
                    className={`notification-toggle ${notificationsEnabled ? 'active' : ''}`}
                    onClick={requestNotificationPermission}
                    title={notificationsEnabled ? "Notifications enabled" : "Enable browser notifications"}
                  >
                    <span className="notification-icon">{notificationsEnabled ? '🔔' : '🔕'}</span>
                  </button>
                  {/* Environment Audio Toggle */}
                  <button 
                    className={`environment-toggle ${isListeningToEnvironment ? 'active' : ''}`}
                    onClick={toggleEnvironmentListening}
                    title={isListeningToEnvironment ? "Stop environment listening" : "Start environment listening"}
                  >
                    <span className="environment-icon">{isListeningToEnvironment ? '🔴' : '🎵'}</span>
                  </button>
                </h3>
                
                <div className="visual-observations-list">
                  {/* Visual Observations - Prioritize camera observations first */}
                  {(() => {
                    // Get pure camera observations from autoPrompts (highest priority)
                    const cameraObservations = autoPrompts.filter(prompt => 
                      !prompt.startsWith('[SCREEN]') && 
                      prompt !== "Processing new image..." &&
                      prompt !== "Camera starting - visual analysis will begin shortly..." &&
                      prompt !== "Camera is analyzing..." &&
                      prompt.trim().length > 0
                    );
                    
                    // Get extracted visual observations from screen data (lower priority)
                    const extractedFromScreen = extractVisualFromScreenObservations();
                    
                    // Prioritize camera observations
                    const allVisualObservations = [...cameraObservations, ...extractedFromScreen];
                    const uniqueVisualObservations = [...new Set(allVisualObservations)]; // Remove duplicates
                    
                    console.log("👁️ Visual display results:", {
                      cameraObservations: cameraObservations.length,
                      extractedFromScreen: extractedFromScreen.length,
                      autoPromptsTotal: autoPrompts.length,
                      uniqueTotal: uniqueVisualObservations.length
                    });
                    
                    return uniqueVisualObservations.length > 0 ? (
                      <div>
                        <ul>
                          {uniqueVisualObservations.slice(0, 6).map((prompt, index) => (
                            <li key={index} className="visual-observation">
                              {cameraObservations.includes(prompt) && <span className="source-tag camera">📷</span>}
                              {extractedFromScreen.includes(prompt) && !cameraObservations.includes(prompt) && <span className="source-tag extracted">🔄</span>}
                              {prompt}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div>
                        <p className="empty-state">
                          {isCameraActive ? "Camera processing - visual observations will appear shortly..." : "Enable camera to see visual observations"}
                        </p>
                        {/* Show debug info when no observations */}
                        <div className="debug-info" style={{marginTop: '10px', fontSize: '0.8rem', color: '#ffd43b'}}>
                          <p>Camera Active: {isCameraActive ? 'Yes' : 'No'}</p>
                          <p>AutoPrompts: {autoPrompts.length} items</p>
                          <p>Camera Observations: {cameraObservations.length}</p>
                          <p>Screen Extracted: {extractedFromScreen.length}</p>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Simplified debug section */}
                  <div className="debug-section" style={{marginTop: '15px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px', fontSize: '0.75rem'}}>
                    <strong>🔍 Debug Info:</strong>
                    {(() => {
                      const cameraObs = autoPrompts.filter(p => !p.startsWith('[SCREEN]') && p !== "Processing new image...");
                      const extractedObs = extractVisualFromScreenObservations();
                      
                      return (
                        <div style={{margin: '5px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px'}}>
                          <div style={{color: '#51cf66'}}>📷 Camera: {cameraObs.length}</div>
                          <div style={{color: '#ffd43b'}}>🔄 Extracted: {extractedObs.length}</div>
                          <div style={{color: '#e599f7'}}>📋 AutoPrompts: {autoPrompts.length}</div>
                          <div style={{color: '#6bcfff'}}>🎯 Active Camera: {isCameraActive ? 'Yes' : 'No'}</div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Audio Observations */}
                  {environmentAudio.length > 0 && (
                    <div className="audio-section">
                      <h4 className="observation-type-title">🎵 Audio (Environment)</h4>
                      <ul>
                        {environmentAudio.slice(0, 3).map((observation, index) => (
                          <li key={index} className="audio-observation">{observation}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Column - Chat + Screen Observations */}
            <div className="right-column">
              {/* Chat Section */}
              <div className="chat-section">
                <div className="conversation-panel">
                  <div className="conversation-history">
                    {conversation.length > 0 ? (
                      conversation.map((msg, index) => (
                        <div 
                          key={index} 
                          className={`message ${msg.role === 'user' ? 'user-message' : msg.role === 'system' ? 'system-message' : 'assistant-message'}`}
                        >
                          <div className="message-content">{msg.content}</div>
                          <div className="message-info">
                            {msg.isTyping && (
                              <span className="typing-indicator"></span>
                            )}
                            {msg.isError && (
                              <span className="error-indicator">Error</span>
                            )}
                            <span className="message-timestamp">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </span>
                            {msg.provider && (
                              <span className={`provider-badge-msg ${msg.provider}`}>
                                {msg.provider === 'openai' ? 'OpenAI' : msg.provider === 'gemini' ? 'Gemini' : 'Merged'}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-chat">
                        <p>Start a conversation with Galactus 3...</p>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  <div className="input-area">
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Ask Galactus about what you see..."
                      disabled={isProcessing}
                      className="message-input"
                    />
                    <button 
                      onClick={sendMessage}
                      disabled={isProcessing || !input.trim()}
                      className="send-button"
                    >
                      {isProcessing ? '...' : '→'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Screen Observations - Scrollable under chat */}
              <div className="screen-observations-panel">
                <h3>
                  🖥️ Enhanced Screen Analysis
                  {isLiveScreenMonitoring && <span className="live-indicator">📡 LIVE</span>}
                </h3>
                
                <div className="screen-observations-scroll">
                  {/* Multi-Modal Observations */}
                  {multiModalObservations.length > 0 && (
                    <div className="observation-subsection">
                      <h4 className="subsection-title">🌐 Multi-Modal Insights</h4>
                      <ul>
                        {multiModalObservations.slice(0, 2).flatMap(obs => 
                          obs.combined_analysis.slice(0, 2).map((analysis, index) => (
                            <li key={`mm-${obs.id}-${index}`} className="multi-modal-observation">{analysis}</li>
                          ))
                        )}
                      </ul>
                    </div>
                  )}
                  
                  {/* Screen Observations - Pure screen content only */}
                  {(() => {
                    const pureScreenObs = getPureScreenObservations();
                    return pureScreenObs.length > 0 ? (
                      <div className="observation-subsection">
                        <h4 className="subsection-title">
                          🖥️ PURE SCREEN: {screenAnalysisMode.replace('_', ' ').toUpperCase()}
                        </h4>
                        <ul>
                          {pureScreenObs.slice(0, 1).flatMap(obs => 
                            obs.auto_prompts.slice(0, 4).map((prompt, index) => (
                              <li key={`pure-screen-${obs.id}-${index}`} className="screen-observation">
                                <span className="content-type-tag">🖥️</span>
                                {prompt}
                              </li>
                            ))
                          )}
                        </ul>
                        
                        {(() => {
                          const totalPrompts = pureScreenObs[0]?.auto_prompts?.length || 0;
                          const extractedVisualCount = extractVisualFromScreenObservations().length;
                          
                          return (
                            <div className="filtering-info">
                              {totalPrompts > 4 && (
                                <p className="more-observations">+ {totalPrompts - 4} more screen observations...</p>
                              )}
                              {extractedVisualCount > 0 && (
                                <p className="filter-status">✨ {extractedVisualCount} visual observations moved to left panel</p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="observation-subsection">
                        <h4 className="subsection-title">🖥️ SCREEN ANALYSIS</h4>
                        <p className="empty-state">
                          {screenObservations.length > 0 
                            ? "All screen content was visual - moved to left panel" 
                            : "Start screen capture to see analysis..."
                          }
                        </p>
                      </div>
                    );
                  })()}
                  
                  {screenObservations.length === 0 && multiModalObservations.length === 0 && (
                    <p className="empty-state">Start screen capture to see enhanced analysis...</p>
                  )}
                  
                  {/* Debug: Show what's in screenObservations - TEMPORARY */}
                  <div className="debug-section" style={{marginTop: '10px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px', fontSize: '0.75rem'}}>
                    <strong>🔍 DEBUG - screenObservations ({screenObservations.length} total):</strong>
                    {screenObservations.length === 0 ? (
                      <p style={{margin: '5px 0', color: '#ffd43b'}}>screenObservations array is empty</p>
                    ) : (
                      <ul style={{margin: '5px 0', listStyle: 'none', padding: 0}}>
                        {screenObservations.map((obs, index) => (
                          <li key={index} style={{margin: '2px 0', color: '#6bcfff'}}>
                            <strong>Observation {index + 1}:</strong> Mode: {obs.analysis_mode}, Prompts: {obs.auto_prompts?.length || 0}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Voice Settings Panel */}
          <div className="voice-settings-panel">
            <h3>
              Voice Response
              <button 
                className={`voice-toggle ${voiceEnabled ? 'active' : ''}`}
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                title={voiceEnabled ? "Disable voice responses" : "Enable voice responses"}
              >
                <span className="voice-icon">{isSpeaking ? '🔊' : voiceEnabled ? '🎤' : '🔇'}</span>
              </button>
            </h3>
            
            {voiceEnabled && (
              <div className="voice-controls">
                <div className="voice-selector">
                  <label htmlFor="voice-select">Voice:</label>
                  <select 
                    id="voice-select"
                    value={selectedVoice} 
                    onChange={(e) => setSelectedVoice(e.target.value)}
                    className="voice-dropdown"
                  >
                    {availableVoices.map((voice) => (
                      <option key={voice.voice_id} value={voice.voice_id}>
                        {voice.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="voice-commands-section">
                  <div className="voice-commands-header">
                    <label>Voice Commands:</label>
                    <button 
                      className={`voice-commands-toggle ${voiceCommandsEnabled ? 'active' : ''}`}
                      onClick={toggleVoiceCommands}
                      title={voiceCommandsEnabled ? "Disable voice commands" : "Enable voice commands"}
                    >
                      {isListeningForCommand ? '🎙️' : voiceCommandsEnabled ? '⚡' : '💬'}
                    </button>
                  </div>
                  
                  {voiceCommandsEnabled && (
                    <div className="voice-commands-help">
                      <p className="commands-intro">Say "Hey Galactus" + command:</p>
                      <ul className="commands-list">
                        <li>"switch to back camera"</li>
                        <li>"go full screen"</li>
                        <li>"what do you see?"</li>
                        <li>"analyze my screen"</li>
                        <li>"monitor my screen" - Start live monitoring</li>
                        <li>"code review mode" - Switch to code analysis</li>
                        <li>"document analysis mode" - Analyze documents</li>
                        <li>"ui analysis mode" - Analyze interfaces</li>
                        <li>"enable multi modal" - Combined analysis</li>
                        <li>"record my screen"</li>
                      </ul>
                      {isListeningForCommand && (
                        <div className="listening-indicator">
                          <span className="listening-icon">👂</span>
                          <span>Listening for "Hey Galactus"...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="voice-status">
                  {isSpeaking && (
                    <div className="speaking-indicator">
                      <span className="speaking-animation">🎙️</span>
                      <span>Speaking...</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Screen Capture Panel */}
          <div className="screen-capture-panel">
            <h3>
              Enhanced Screen Analysis
              <button 
                className={`screen-toggle ${isScreenCaptureActive ? 'active' : ''}`}
                onClick={isScreenCaptureActive ? stopScreenCapture : startScreenCapture}
                title={isScreenCaptureActive ? "Stop screen capture" : "Start screen capture"}
              >
                <span className="screen-icon">{isScreenCaptureActive ? '🛑' : '🖥️'}</span>
              </button>
            </h3>
            
            {isScreenCaptureActive && (
              <div className="screen-controls">
                {/* Analysis Mode Selector */}
                <div className="analysis-mode-selector">
                  <label htmlFor="analysis-mode">Analysis Mode:</label>
                  <select 
                    id="analysis-mode"
                    value={screenAnalysisMode} 
                    onChange={(e) => setScreenAnalysisMode(e.target.value)}
                    className="analysis-dropdown"
                  >
                    <option value="general">General Analysis</option>
                    <option value="code_review">Code Review</option>
                    <option value="document_analysis">Document Analysis</option>
                    <option value="ui_analysis">UI/UX Analysis</option>
                  </select>
                </div>

                {/* Live Screen Monitoring */}
                <div className="live-monitoring-section">
                  <button 
                    className={`live-monitoring-button ${isLiveScreenMonitoring ? 'active' : ''}`}
                    onClick={toggleLiveScreenMonitoring}
                    title={isLiveScreenMonitoring ? "Stop live screen monitoring" : "Start live screen monitoring (every 8 seconds)"}
                  >
                    <span className="monitoring-icon">{isLiveScreenMonitoring ? '⏸️' : '🔄'}</span>
                    {isLiveScreenMonitoring ? 'Stop Live Monitoring' : 'Start Live Monitoring'}
                  </button>
                  {isLiveScreenMonitoring && (
                    <p className="monitoring-status">📡 Auto-analyzing every 8 seconds</p>
                  )}
                </div>

                {/* Multi-Modal Analysis */}
                <div className="multi-modal-section">
                  <label className="multi-modal-label">
                    <input 
                      type="checkbox"
                      checked={multiModalEnabled}
                      onChange={(e) => setMultiModalEnabled(e.target.checked)}
                    />
                    <span className="multi-modal-text">
                      🌐 Multi-Modal Analysis (Camera + Screen)
                    </span>
                  </label>
                  {multiModalEnabled && (
                    <p className="multi-modal-status">Combining visual and screen observations</p>
                  )}
                </div>
                
                <div className="screen-actions">
                  <button 
                    className="screen-action-button analyze"
                    onClick={() => captureScreen()}
                    disabled={isProcessing}
                    title={`Analyze current screen content (${screenAnalysisMode} mode)`}
                  >
                    <span className="action-icon">🔍</span>
                    Analyze Screen ({screenAnalysisMode})
                  </button>
                  
                  <button 
                    className={`screen-action-button record ${isRecording ? 'active' : ''}`}
                    onClick={startScreenRecording}
                    disabled={isRecording}
                    title="Record screen (30 seconds)"
                  >
                    <span className="action-icon">{isRecording ? '⏹️' : '🎥'}</span>
                    {isRecording ? 'Recording...' : 'Record Screen'}
                  </button>

                  {multiModalEnabled && isCameraActive && (
                    <button 
                      className="screen-action-button multi-modal"
                      onClick={createMultiModalAnalysis}
                      disabled={isProcessing}
                      title="Create combined camera + screen analysis"
                    >
                      <span className="action-icon">🌐</span>
                      Multi-Modal Analysis
                    </button>
                  )}
                </div>
                
                <div className="screen-status">
                  <div className="screen-info">
                    <span className="status-text">
                      {isProcessing ? `Analyzing screen (${screenAnalysisMode})...` : 
                       isLiveScreenMonitoring ? 'Live monitoring active' : 'Screen capture active'}
                    </span>
                  </div>
                  
                  {isRecording && (
                    <div className="recording-indicator">
                      <span className="recording-dot">🔴</span>
                      <span>Recording (30s limit)</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {!isScreenCaptureActive && (
              <div className="screen-help">
                <p className="help-text">Enhanced screen analysis features:</p>
                <ul className="help-list">
                  <li>• <strong>Live Monitoring:</strong> Auto-analyze every 8 seconds</li>
                  <li>• <strong>Analysis Modes:</strong> General, Code Review, Document, UI/UX</li>
                  <li>• <strong>Multi-Modal:</strong> Combined camera + screen insights</li>
                  <li>• <strong>Voice Commands:</strong> "Hey Galactus, monitor my screen"</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="galactus-footer">
        <p>© 2025 Galactus 3 System | All rights reserved</p>
      </footer>
      
      {/* Full-screen camera overlay */}
      {isFullScreen && (
        <div className="fullscreen-overlay">
          <div className="fullscreen-camera-container">
            {/* Full-screen camera view */}
            <div className="fullscreen-webcam">
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                width="100%"
                height="100%"
                style={{ objectFit: 'cover' }}
                videoConstraints={{
                  facingMode: facingMode
                }}
              />
            </div>
            
            {/* Full-screen controls - top overlay */}
            <div className="fullscreen-controls-top">
              <button
                className="fullscreen-control-button exit-fullscreen"
                onClick={() => setIsFullScreen(false)}
                title="Exit full-screen mode"
              >
                ✕
              </button>
              
              <button
                className="fullscreen-control-button flip-camera"
                onClick={toggleCameraFacing}
                title={facingMode === "user" ? "Switch to back camera" : "Switch to front camera"}
              >
                🔄
              </button>
            </div>
            
            {/* Enhanced Live observations feed - Instagram Live style */}
            <div className="live-observations-feed">
              {/* Multi-Modal Observations */}
              {multiModalObservations.slice(0, 2).flatMap(obs => 
                obs.combined_analysis.slice(0, 1).map((analysis, index) => (
                  <div 
                    key={`mm-${obs.id}-${index}`} 
                    className="live-observation multi-modal"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <span className="observation-type-icon">🌐</span>
                    <span className="observation-text">{analysis}</span>
                  </div>
                ))
              )}
              
              {/* Screen Observations */}
              {screenObservations.slice(0, 2).flatMap(obs => 
                obs.auto_prompts.slice(0, 1).map((prompt, index) => (
                  <div 
                    key={`screen-${obs.id}-${index}`} 
                    className="live-observation screen"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <span className="observation-type-icon">🖥️</span>
                    <span className="observation-text">{prompt}</span>
                  </div>
                ))
              )}
              
              {/* Visual Observations */}
              {autoPrompts.filter(prompt => !prompt.startsWith('[SCREEN]')).slice(0, 2).map((prompt, index) => (
                <div 
                  key={`visual-${index}`} 
                  className="live-observation visual"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <span className="observation-type-icon">👁️</span>
                  <span className="observation-text">{prompt}</span>
                </div>
              ))}
              
              {/* Audio Observations */}
              {environmentAudio.slice(0, 1).map((observation, index) => (
                <div 
                  key={`audio-${index}`} 
                  className="live-observation audio"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <span className="observation-type-icon">🎵</span>
                  <span className="observation-text">{observation}</span>
                </div>
              ))}
            </div>
            
            {/* Mini chat interface - bottom right */}
            <div className="mini-chat">
              <div className="mini-chat-header">
                <span>Ask Galactus</span>
                <span className="live-indicator">🔴 LIVE</span>
              </div>
              
              <div className="mini-chat-messages" ref={miniChatMessagesRef}>
                {conversation.slice(-3).map((msg, index) => (
                  <div key={index} className={`mini-message ${msg.role}`}>
                    <span className="message-content">{msg.content}</span>
                  </div>
                ))}
              </div>
              
              <div className="mini-chat-input">
                <input
                  type="text"
                  placeholder="Ask about what you see..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMiniChatMessage()}
                  className="mini-input-field"
                />
                <button 
                  onClick={sendMiniChatMessage} 
                  disabled={isLoading || !inputMessage.trim()}
                  className="mini-send-button"
                >
                  →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      {/* Temporarily disable starfield for performance */}
      {/* <Starfield /> */}
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/test" element={<TestPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
